import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import t from 'prop-types';
import cn from 'classnames';
import 'raf';
import Portal from '@mos-cat/portal';

import PopoverWrapperContent from './private/PopoverWrapperContent';
import {
  APPEARANCE_DURATION,
  CHECK_CHILD_INTERVAL,
  FULL_HEADER_HEIGHT_CHANGE_EVENT,
  HEADER_INIT_EVENT,
  POSITIONS,
} from './private/constants';
import {
  getElementDocumentOffset,
  getHeaderRealHeight,
  getPopoverPointerStyle,
  getPopoverPosition,
  getPopoverStyle,
} from './private/helpers';

import './styles/PopoverWrapper.pcss';

class PopoverWrapper extends PureComponent {
  constructor(props) {
    super(props);

    this.animationFrameId = null;
    this.checkCoordinatesIntervalId = null;

    this.childRef = React.createRef();
    this.portalRef = React.createRef();
    this.pointerRef = React.createRef();

    this.invalidate = this.invalidate.bind(this);
    this.validateRefs = this.validateRefs.bind(this);
    this.handleHeaderHeightChange = this.handleHeaderHeightChange.bind(this);
    this.setChildCheckInterval = this.setChildCheckInterval.bind(this);
    this.setChildCoordinates = this.setChildCoordinates.bind(this);
    this.subscribeHeaderHeightChange = this.subscribeHeaderHeightChange.bind(
      this
    );
    this.adjustPopoverSize = this.adjustPopoverSize.bind(this);

    this.state = {
      headerHeight: 0,
      childRect: {},
      pointerClassName: 'PopoverWrapper__pointer',
    };
  }

  componentDidMount() {
    const { open, trackChildPosition, considerHeader } = this.props;

    // Контейнер поповера
    const portalElement = this.portalRef.current.element;
    portalElement.className = 'PopoverWrapper__portal';

    this.invalidate();

    window.addEventListener('resize', this.invalidate);

    // Подписка на события хэдера
    if (considerHeader) {
      if (window.$mos_layouts && window.$mos_layouts.on) {
        // Если хэдер уже проинициализирован
        this.subscribeHeaderHeightChange();
      } else {
        // Если хэдер ещё не был проинициализирован
        document.addEventListener(
          HEADER_INIT_EVENT,
          this.subscribeHeaderHeightChange,
          false
        );
      }
    }

    // Запуск проверки изменения положения только для открытого поповера
    if (trackChildPosition && open) {
      this.setChildCheckInterval();
    }
  }

  componentDidUpdate(prevProps) {
    const { open, trackChildPosition } = this.props;
    const justOpened = !prevProps.open && open;
    const justClosed = prevProps.open && !open;
    const trackChildPositionEnabled =
      !prevProps.trackChildPosition && trackChildPosition;
    const trackChildPositionDisabled =
      prevProps.trackChildPosition && !trackChildPosition;
    const childCheckByOpenEnabled = trackChildPosition && justOpened;
    const childCheckByPropsEnabled = trackChildPositionEnabled && open;

    // Запуск проверки координат для открывшегося поповера или по свойству
    if (childCheckByOpenEnabled || childCheckByPropsEnabled) {
      this.setChildCheckInterval();
    }

    // Остановка проверки координат для закрывшегося поповера или по свойству
    if (justClosed || trackChildPositionDisabled) {
      window.clearInterval(this.checkCoordinatesIntervalId);
    }

    this.invalidate();
  }

  componentWillUnmount() {
    const { considerHeader } = this.props;
    const { animationFrameId, checkCoordinatesIntervalId } = this;

    window.clearInterval(checkCoordinatesIntervalId);
    window.cancelAnimationFrame(animationFrameId);
    window.removeEventListener('resize', this.invalidate);

    // Отписка от событий хэдера
    if (considerHeader) {
      document.removeEventListener(
        HEADER_INIT_EVENT,
        this.subscribeHeaderHeightChange
      );
      if (window.$mos_layouts && window.$mos_layouts.off) {
        window.$mos_layouts.off(
          FULL_HEADER_HEIGHT_CHANGE_EVENT,
          this.handleHeaderHeightChange
        );
      }
    }
  }

  setPopoverStyle() {
    if (!this.validateRefs()) {
      return;
    }
    const { appearanceDuration } = this.props;
    const { element: portalElement } = this.portalRef.current;
    portalElement.style.transition = `visibility ${appearanceDuration}ms, opacity ${appearanceDuration}ms`;
  }

  setPopoverClassName(position) {
    if (!this.validateRefs()) {
      return;
    }
    const { open, modal, className } = this.props;
    const { element: portalElement } = this.portalRef.current;
    portalElement.className = cn(
      'PopoverWrapper__portal',
      `PopoverWrapper__position_${position}`,
      {
        PopoverWrapper__portal_visible: open,
        PopoverWrapper__portal_modal: modal,
      },
      className
    );
  }

  setPopoverPosition(position, childElement) {
    if (!this.validateRefs()) {
      return;
    }
    const { floating } = this.props;
    const { element: portalElement } = this.portalRef.current;
    const { top, right, bottom, left, transform } = getPopoverStyle(
      position,
      floating,
      portalElement,
      childElement
    );

    portalElement.style.top = top;
    portalElement.style.right = right;
    portalElement.style.bottom = bottom;
    portalElement.style.left = left;
    portalElement.style.transform = transform;
  }

  setPointerClassName(position) {
    this.setState({
      pointerClassName: cn(
        'PopoverWrapper__pointer',
        `PopoverWrapper__pointer-${position}`
      ),
    });
  }

  setPointerPosition(position, childElement) {
    if (!this.validateRefs()) {
      return;
    }
    const { floating } = this.props;
    const { pointerRef, portalRef } = this;
    const { element: portalElement } = portalRef.current;
    const pointerElement = pointerRef.current;

    const { top, right, bottom, left } = getPopoverPointerStyle(
      position,
      floating,
      portalElement,
      childElement,
      pointerElement
    );

    pointerElement.style.top = top;
    pointerElement.style.right = right;
    pointerElement.style.bottom = bottom;
    pointerElement.style.left = left;
  }

  setChildCheckInterval() {
    this.checkCoordinatesIntervalId = window.setInterval(
      this.setChildCoordinates,
      CHECK_CHILD_INTERVAL
    );
  }

  setChildCoordinates() {
    const { childRect } = this.state;
    const { childRef } = this;
    /* eslint-disable-next-line react/no-find-dom-node */
    const childElement = ReactDOM.findDOMNode(childRef.current);
    const newChildRect = getElementDocumentOffset(childElement);
    const rectChanged =
      newChildRect.top !== childRect.top ||
      newChildRect.right !== childRect.right ||
      newChildRect.bottom !== childRect.bottom ||
      newChildRect.left !== childRect.left;

    if (rectChanged) {
      this.setState({ childRect: newChildRect });
    }
  }

  adjustPopoverSize(childElement) {
    const { adjustPopoverSize } = this.props;
    if (!adjustPopoverSize || !this.validateRefs()) return;
    const { element: portalElement } = this.portalRef.current;
    const { clientWidth: width } = childElement;
    const { borderLeftWidth, borderRightWidth } = window.getComputedStyle(
      childElement
    );
    portalElement.style.width = `${width +
      parseFloat(borderLeftWidth) +
      parseFloat(borderRightWidth)}px`;
  }

  validateRefs() {
    const { portalRef, pointerRef, childRef } = this;
    return portalRef.current && pointerRef.current && childRef.current;
  }

  invalidate() {
    const { modal, position, floating } = this.props;
    const { headerHeight } = this.state;
    const { childRef, portalRef } = this;

    if (!this.validateRefs()) {
      return;
    }

    const { element: portalElement } = portalRef.current;
    /* eslint-disable-next-line react/no-find-dom-node */
    const childElement = ReactDOM.findDOMNode(childRef.current);

    window.cancelAnimationFrame(this.animationFrameId);

    this.animationFrameId = window.requestAnimationFrame(() => {
      const calculatedPosition = getPopoverPosition(
        position,
        modal,
        floating,
        headerHeight,
        portalElement,
        childElement
      );
      this.adjustPopoverSize(childElement);
      this.setPopoverStyle();
      this.setPopoverClassName(calculatedPosition);
      this.setPopoverPosition(calculatedPosition, childElement);
      this.setPointerClassName(calculatedPosition);
      this.setPointerPosition(calculatedPosition, childElement);
    });
  }

  subscribeHeaderHeightChange() {
    window.$mos_layouts.on(
      FULL_HEADER_HEIGHT_CHANGE_EVENT,
      this.handleHeaderHeightChange
    );
  }

  handleHeaderHeightChange() {
    this.setState({ headerHeight: getHeaderRealHeight() });
  }

  render() {
    const { children, content } = this.props;
    const { pointerClassName } = this.state;
    const { childRef, portalRef, pointerRef } = this;
    const child = React.Children.only(children);
    return (
      <React.Fragment>
        <PopoverWrapperContent ref={childRef}>{child}</PopoverWrapperContent>
        <Portal refProp={portalRef}>
          <div ref={pointerRef} className={pointerClassName} />
          {content}
        </Portal>
      </React.Fragment>
    );
  }
}

if (process.env.NODE_ENV !== 'production') {
  PopoverWrapper.propTypes = {
    /**
     * Дополнительный className портала
     */
    className: t.string,
    /**
     * Длительность (в миллисекундах) анимации при показе/сокрытии тултипа.
     */
    appearanceDuration: t.number,
    /**
     * Ребёнок к которому необходимо прицепить тултип
     */
    children: t.node.isRequired,
    /**
     * Содержимое поповера
     */
    content: t.node.isRequired,
    position: t.oneOf(Object.values(POSITIONS)),
    open: t.bool,
    /**
     * Флаг, указывающий на то, что поповер отображается для модального окна.
     */
    modal: t.bool,
    /**
     * Флаг, указывающий на необходимость поповеру автоматически менять положение, если он не
     * помещается в пределах экрана.
     */
    floating: t.bool,
    /**
     * Приравнивать или нет хэдер mos.ru к границе документа.
     */
    considerHeader: t.bool,
    /**
     * Отслеживать пололжение элемента, к которому привязан поповер.
     */
    trackChildPosition: t.bool,
    /**
     * Растягивать поповер по ширине эелемента, к которому привязан поповер.
     */
    adjustPopoverSize: t.bool,
  };
}

PopoverWrapper.defaultProps = {
  position: POSITIONS.TOP,
  open: false,
  modal: false,
  className: '',
  appearanceDuration: APPEARANCE_DURATION,
  floating: false,
  considerHeader: false,
  trackChildPosition: true,
  adjustPopoverSize: false,
};

export { PopoverWrapper as default, POSITIONS, APPEARANCE_DURATION };
